import React, { useState, createContext, useContext, useEffect } from 'react';

/**
 * @typedef {Object} Ids
 * @property {string} module
 * @property {string} heading
 * @property {string} content
 *
 * @typedef {Object} CollapsibleContextType
 * @property {Ids} ids
 * @property {boolean} isOpened
 * @property {(value: boolean | ((prev: boolean) => boolean)) => void} setIsOpened
 * @property {boolean} isClosable
 * @property {boolean} hasToggle
 */

/** @type {React.Context<CollapsibleContextType>} */
const Context = createContext({
    ids: {
        module: '',
        heading: '',
        content: ''
    },
    isOpened: false,
    setIsOpened: () => { },
    isClosable: true,
    hasToggle: true
});
Context.displayName = 'CollapsibleContext';

/**
 * @typedef {Object} CollapsibleProps
 * @property {string} id
 * @property {boolean} [isOpened]
 * @property {boolean} [isClosable]
 * @property {boolean} [hasToggle]
 * @property {string} [className]
 * @property {(state: { isOpened: boolean }) => void} [getState]
 * @property {React.ReactNode} [children]
 */

/**
 * @type {React.FC<CollapsibleProps>}
 */
const Collapsible = props => {
    const [ids] = useState({
        module: props.id,
        heading: props.id + '-heading',
        content: props.id + '-content'
    });
    const [isOpened, setIsOpened] = useState(!!props.isOpened);
    const [isClosable] = useState(props.isClosable ?? true);
    const [hasToggle] = useState(props.hasToggle ?? true);
    const context = {
        ids,
        isOpened,
        setIsOpened,
        isClosable,
        hasToggle
    };

    useEffect(() => {
        if (props.getState) {
            props.getState({ isOpened });
        }
    }, [isOpened]);

    return (
        <Context.Provider value={context}>
            <div
                className={props.className}
                id={ids.module}>
                {props.children}
            </div>
        </Context.Provider>
    );
};

/**
 * @typedef {Object} HeadingProps
 * @property {string} [className]
 * @property {React.ReactNode} [children]
 */

/**
 * @type {React.FC<HeadingProps>}
 */
const Heading = props => {
    const { ids, isOpened, setIsOpened, isClosable } = useContext(Context);

    return (
        <button
            aria-controls={ids.content}
            aria-disabled={isOpened && !isClosable ? 'true' : 'false'}
            className={props.className}
            data-expanded={isOpened ? 'true' : 'false'}
            disabled={isOpened && !isClosable}
            id={ids.heading}
            onClick={() => (!isOpened || isClosable) && setIsOpened(!isOpened)}
            type='button'>
            {props.children}
        </button>
    );
};

/**
 * @typedef {Object} ContentProps
 * @property {string} [className]
 * @property {React.ReactNode} [children]
 */

/**
 * @type {React.FC<ContentProps>}
 */
const Content = props => {
    const { ids, isOpened, hasToggle } = useContext(Context);

    return (
        <div
            aria-hidden={!isOpened}
            aria-labelledby={ids.heading}
            className={props.className}
            hidden={!isOpened && hasToggle}
            id={ids.content}>
            {props.children}
        </div>
    );
};

const CollapsibleContextConsumer = Context.Consumer;

export {
    Collapsible,
    Heading as CollapsibleHeading,
    Content as CollapsibleContent,
    Context as CollapsibleContext,
    CollapsibleContextConsumer
};
